/**
 * `VesselLiveTrack` — heading-rotated AIS marker, fading 50-segment
 * trail polyline, and overlay/bottom-sheet vessel status panel.
 *
 * Architecture:
 *   - `useVesselTrail(mmsi)` returns the trail buffer (oldest -> newest,
 *     capped at 50) plus SSE status. The hook is the single source of
 *     truth for both the polyline and the marker rotation.
 *   - The MapLibre instance + HTML marker live in refs. React only
 *     drives them via effects that read the latest `points` array;
 *     no per-frame React re-render of the map canvas itself.
 *   - The trail uses MapLibre data-driven styling: the GeoJSON source
 *     stores per-segment opacity (oldest 0.10, newest 0.90, linear)
 *     and the line layer uses `["get", "opacity"]` to render the fade.
 *   - The marker is a plain `<div>` with an inline SVG; `transform:
 *     rotate(<headingDeg>deg)` is applied to the wrapper on every new
 *     fix so the visible orientation matches AIS heading.
 *   - Map auto-pans (easeTo, 600ms) only when the marker drifts more
 *     than half-viewport off-centre; this avoids constant panning
 *     while the vessel is centred.
 *
 * Mobile: `< sm` viewports hide the overlay panel and render a
 * `VesselBottomSheet` triggered by tapping the marker. The sheet
 * is dismissable via Escape or backdrop click.
 *
 * Zero-mock policy: every coordinate comes from `ship.recentPositions`
 * (Postgres) or `/sse/ships/:mmsi` (Redis bridge → AISStream).
 */

import type { Feature, FeatureCollection, LineString } from "geojson";
import maplibregl, {
  type GeoJSONSource,
  type LngLatLike,
  type Map as MapLibreMap,
  type Marker as MapLibreMarker,
} from "maplibre-gl";
import { useEffect, useRef, useState } from "react";

import { useVesselTrail } from "#/hooks/useVesselTrail.js";
import type { TranslateFn } from "#/lib/i18n.js";

import { VesselBottomSheet } from "./VesselBottomSheet.js";
import { VesselOverlayPanel } from "./VesselOverlayPanel.js";

const MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/dark";
const DEFAULT_CENTER: LngLatLike = [0, 20];
const DEFAULT_ZOOM = 2;
const MARKER_VIEWPORT_PAN_THRESHOLD = 0.5;
const PAN_DURATION_MS = 600;
const SR_ANNOUNCE_THROTTLE_MS = 30_000;
const TRAIL_OPACITY_NEWEST = 0.9;
const TRAIL_OPACITY_OLDEST = 0.1;

export interface VesselLiveTrackProps {
  /** AIS MMSI of the vessel to track. Renders a fallback when null. */
  mmsi: string | null | undefined;
  /** Locale (en / ar / fr) — drives Intl formatters + tile labels. */
  locale: string;
  /** Translation function (vesselTrack namespace). */
  t: TranslateFn;
  /** Override API base URL — used by tests. */
  apiBaseUrl?: string;
  /** Override map style URL — used by tests / themes. */
  styleUrl?: string;
  /** Inject a different MapLibre module (test seam). */
  mapLib?: typeof maplibregl;
}

/** Build a GeoJSON FeatureCollection of trail segments with per-segment opacity. */
export function buildTrailGeojson(
  coords: Array<[number, number]>,
  newest: number = TRAIL_OPACITY_NEWEST,
  oldest: number = TRAIL_OPACITY_OLDEST,
): FeatureCollection<LineString> {
  if (coords.length < 2) {
    return { type: "FeatureCollection", features: [] };
  }
  const features: Array<Feature<LineString>> = [];
  const segCount = coords.length - 1;
  for (let i = 0; i < segCount; i += 1) {
    const t = segCount === 1 ? 1 : i / (segCount - 1);
    const opacity = oldest + (newest - oldest) * t;
    features.push({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [coords[i]!, coords[i + 1]!],
      },
      properties: { opacity, index: i },
    });
  }
  return { type: "FeatureCollection", features };
}

/** Spinner mark used by the freshness banner when no data has landed yet. */
function Loader2({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

/** Build the heading-rotation wrapper DOM for the AIS marker. */
function buildMarkerElement(label: string): HTMLDivElement {
  const wrapper = document.createElement("div");
  wrapper.className = "vessel-live-marker";
  wrapper.setAttribute("role", "button");
  wrapper.tabIndex = 0;
  wrapper.setAttribute("aria-label", label);
  wrapper.style.cssText = [
    "width:32px",
    "height:32px",
    "display:inline-block",
    "transform-origin:50% 50%",
    "transition:transform 100ms linear",
    "color:hsl(354 78% 52%)",
    "cursor:pointer",
  ].join(";");
  // Inline SVG so the file works offline + has no asset 404 risk.
  wrapper.innerHTML = [
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32" aria-hidden="true" focusable="false">',
    '<circle cx="16" cy="16" r="15" fill="hsl(0 0% 0% / 0.18)" />',
    '<path d="M16 2 L26 26 L16 21 L6 26 Z" fill="currentColor" stroke="hsl(0 0% 100%)" stroke-width="1.5" stroke-linejoin="round" />',
    '<circle cx="16" cy="16" r="2" fill="hsl(0 0% 100%)" />',
    "</svg>",
  ].join("");
  return wrapper;
}

/** Apply heading rotation to the marker wrapper. */
function rotateMarker(el: HTMLElement, headingDeg: number | null | undefined): void {
  const deg = typeof headingDeg === "number" ? headingDeg : 0;
  el.style.transform = `rotate(${deg}deg)`;
  el.setAttribute("data-heading", String(deg));
}

/** Format a verifiedAt timestamp into a localised relative string. */
function relativeTime(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    const diffSec = Math.round((ms - Date.now()) / 1000);
    const abs = Math.abs(diffSec);
    if (abs < 60) return rtf.format(diffSec, "second");
    if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
    if (abs < 86_400) return rtf.format(Math.round(diffSec / 3600), "hour");
    return rtf.format(Math.round(diffSec / 86_400), "day");
  } catch {
    return iso;
  }
}

export function VesselLiveTrack({
  mmsi,
  locale,
  t,
  apiBaseUrl,
  styleUrl,
  mapLib,
}: VesselLiveTrackProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markerRef = useRef<MapLibreMarker | null>(null);
  const markerElRef = useRef<HTMLDivElement | null>(null);
  const lastAnnounceRef = useRef(0);

  const [isStyleLoaded, setIsStyleLoaded] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const trail = useVesselTrail(mmsi, { apiBaseUrl, limit: 50 });
  const { points, name, nextPort, eta, status, retry } = trail;
  const latest = points[points.length - 1] ?? null;

  const tRef = useRef(t);
  tRef.current = t;
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const nameRef = useRef(name);
  nameRef.current = name;

  // ------------------------------------------------------------------
  // 1. Map construction (mount only).
  // ------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const lib = mapLib ?? maplibregl;
    let map: MapLibreMap;
    try {
      map = new lib.Map({
        container,
        style: styleUrl ?? MAP_STYLE_URL,
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        attributionControl: { compact: true },
        pitch: 0,
        bearing: 0,
        pitchWithRotate: false,
        dragRotate: false,
        renderWorldCopies: false,
      });
    } catch {
      setHasFailed(true);
      return undefined;
    }
    mapRef.current = map;

    map.addControl(new lib.NavigationControl({ showCompass: false }), "top-end" as never);
    map.addControl(new lib.ScaleControl({ unit: "metric" }), "bottom-end" as never);

    map.on("error", (e: unknown) => {
      const msg =
        (e as { error?: { message?: string } } | undefined)?.error?.message ?? e;
      console.warn("[VesselLiveTrack]", msg);
    });

    map.on("load", () => {
      // Trail source — one Feature per segment so opacity can vary along
      // the line via data-driven styling (`["get", "opacity"]`).
      map.addSource("vessel-trail", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "vessel-trail-line",
        type: "line",
        source: "vessel-trail",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": "hsl(354 78% 52%)",
          "line-width": 3,
          "line-opacity": ["coalesce", ["get", "opacity"], 0.5],
        },
      });

      setIsStyleLoaded(true);
    });

    return () => {
      markerRef.current?.remove();
      markerRef.current = null;
      markerElRef.current = null;
      map.remove();
      mapRef.current = null;
    };
    // Mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // 2. Sync trail polyline whenever points or style change.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleLoaded) return;
    const source = map.getSource("vessel-trail") as GeoJSONSource | undefined;
    if (!source) return;
    const coords: Array<[number, number]> = points.map((p) => [p.lng, p.lat]);
    source.setData(buildTrailGeojson(coords));
  }, [points, isStyleLoaded]);

  // ------------------------------------------------------------------
  // 3. Sync marker position + heading on every new fix.
  //    Auto-pan to centre when marker drifts > half viewport off centre.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleLoaded || !latest) return;
    const lib = mapLib ?? maplibregl;

    const lngLat: LngLatLike = [latest.lng, latest.lat];
    if (!markerRef.current) {
      const label = tRef.current("markerAriaLabel", {
        name: nameRef.current || "—",
        heading: latest.headingDegrees ?? 0,
      });
      const el = buildMarkerElement(label);
      el.addEventListener("click", () => setSheetOpen((v) => !v));
      el.addEventListener("keydown", (e) => {
        const key = (e as KeyboardEvent).key;
        if (key === "Enter" || key === " ") {
          e.preventDefault();
          setSheetOpen((v) => !v);
        }
      });
      markerElRef.current = el;
      markerRef.current = new lib.Marker({ element: el })
        .setLngLat(lngLat)
        .addTo(map);
      // Initial recentre — never default to (0,20) once we have a fix.
      map.flyTo({
        center: lngLat,
        zoom: Math.max(map.getZoom(), 4),
        essential: true,
      });
    } else {
      markerRef.current.setLngLat(lngLat);
    }

    if (markerElRef.current) {
      rotateMarker(markerElRef.current, latest.headingDegrees);
      markerElRef.current.setAttribute(
        "aria-label",
        tRef.current("markerAriaLabel", {
          name: nameRef.current || "—",
          heading: latest.headingDegrees ?? 0,
        }),
      );
    }

    // Auto-pan when off-centre.
    try {
      const point = map.project(lngLat);
      const canvas = map.getCanvas();
      const dx = Math.abs(point.x - canvas.clientWidth / 2);
      const dy = Math.abs(point.y - canvas.clientHeight / 2);
      const offX = dx > canvas.clientWidth * MARKER_VIEWPORT_PAN_THRESHOLD;
      const offY = dy > canvas.clientHeight * MARKER_VIEWPORT_PAN_THRESHOLD;
      if (offX || offY) {
        map.easeTo({ center: lngLat, duration: PAN_DURATION_MS });
      }
    } catch {
      // Map may not be fully rendered yet — skip the pan check.
    }
  }, [latest, isStyleLoaded, mapLib]);

  // ------------------------------------------------------------------
  // 4. Screen-reader live region — announce "Position updated" at most
  //    once every 30s to avoid AT spam.
  // ------------------------------------------------------------------
  const [srMessage, setSrMessage] = useState("");
  useEffect(() => {
    if (!latest) return;
    const now = Date.now();
    if (now - lastAnnounceRef.current >= SR_ANNOUNCE_THROTTLE_MS) {
      lastAnnounceRef.current = now;
      setSrMessage(t("srPositionUpdated"));
    }
  }, [latest, t]);

  // ------------------------------------------------------------------
  // 5. Render.
  // ------------------------------------------------------------------
  if (!mmsi) {
    return (
      <section
        className="vessel-live-track-fallback rounded-lg border p-4 text-sm"
        aria-label={t("title")}
        style={{
          background: "hsl(220 14% 14%)",
          color: "hsl(215 20% 80%)",
          borderColor: "hsl(220 14% 26%)",
        }}
      >
        {t("empty")}
      </section>
    );
  }

  if (hasFailed) {
    return (
      <section
        className="vessel-live-track-fallback rounded-lg border p-4 text-sm"
        aria-label={t("title")}
        style={{
          background: "hsl(220 14% 14%)",
          color: "hsl(215 20% 80%)",
          borderColor: "hsl(220 14% 26%)",
        }}
      >
        {t("empty")}
      </section>
    );
  }

  const bannerTime = relativeTime(trail.freshestVerifiedAt, locale);
  const bannerLabel = trail.freshestVerifiedAt
    ? t("source.banner", { time: bannerTime ?? trail.freshestVerifiedAt })
    : t("source.bannerNoData");

  return (
    <section
      className="vessel-live-track"
      aria-label={t("title")}
      style={{ position: "relative" }}
    >
      <div
        role="application"
        aria-label={t("mapAriaLabel", { name: name || "—" })}
        ref={containerRef}
        data-testid="vessel-live-track-canvas"
        style={{
          width: "100%",
          height: "440px",
          borderRadius: "0.5rem",
          overflow: "hidden",
          background: "hsl(220 14% 8%)",
        }}
      />

      {/* Loading skeleton until tiles paint. */}
      {!isStyleLoaded && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "hsl(220 14% 10%)",
            borderRadius: "0.5rem",
            color: "hsl(215 20% 70%)",
            fontSize: "0.875rem",
          }}
        >
          {t("loading")}
        </div>
      )}

      {/* Desktop overlay panel — hidden on mobile. */}
      <div
        className="hidden sm:block absolute"
        style={{
          top: "1rem",
          insetInlineEnd: "1rem",
          zIndex: 10,
          maxWidth: "calc(100% - 2rem)",
        }}
      >
        <VesselOverlayPanel
          name={name}
          mmsi={mmsi}
          latest={latest}
          nextPort={nextPort}
          eta={eta}
          status={status}
          onRetry={retry}
          t={t}
          locale={locale}
        />
      </div>

      {/* Source banner — under the map, RTL-correct via logical CSS. */}
      <div
        className="mt-2 flex items-center gap-2 text-xs"
        style={{ color: "hsl(215 20% 70%)" }}
        data-testid="vessel-source-banner"
      >
        {!trail.freshestVerifiedAt && status !== "open" && (
          <Loader2 className="animate-spin" />
        )}
        <span>{bannerLabel}</span>
      </div>

      {/* Mobile bottom sheet */}
      <VesselBottomSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        name={name}
        mmsi={mmsi}
        latest={latest}
        nextPort={nextPort}
        eta={eta}
        status={status}
        onRetry={retry}
        t={t}
        locale={locale}
      />

      {/* Visually-hidden live region for SR "Position updated" announcements. */}
      <p
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          margin: "-1px",
          padding: 0,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {srMessage}
      </p>
    </section>
  );
}
